// src/pages/lgbtqia/IntakeForm.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Grid, TextField, MenuItem, Typography, Alert, Button
} from '@mui/material';

const giOptions = [
  'Cis woman', 'Cis man', 'Trans woman', 'Trans man', 'Non-binary', 'Genderqueer', 'Agender', 'Questioning', 'Prefer not to say'
];
const pronounOptions = ['She/Her', 'He/Him', 'They/Them', 'She/They', 'He/They', 'Other'];
const orientationOptions = [
  'Heterosexual', 'Gay', 'Lesbian', 'Bisexual', 'Pansexual', 'Asexual', 'Queer', 'Questioning', 'Prefer not to say'
];
const sabOptions = ['Female', 'Male', 'Intersex', 'Prefer not to say'];

const useQuery = () => new URLSearchParams(useLocation().search);

export default function IntakeForm() {
  const q = useQuery();
  const [citizenId, setCitizenId] = useState(q.get('citizenId') || '');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    chosenName: '', legalName: '',
    pronouns: '', pronounsOther: '',
    sexAssignedAtBirth: '',
    genderIdentity: '',
    sexualOrientation: '',
    safetyConcerns: '',
    transitionHistory: '',
    hormoneTherapy: '',
    mentalHealthScreen: '',
    socialSupport: '',
    notes: ''
  });

  const storageKey = useMemo(() => `lgbtqia-intake:${citizenId || 'unspecified'}`, [citizenId]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved) setForm({ ...form, ...saved });
    } catch {}
    // eslint-disable-next-line
  }, [storageKey]);

  const handle = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const doSave = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(form));
      setMsg('Saved locally. (Backend wiring can be added later.)');
      setErr('');
    } catch (e) {
      setErr('Unable to save in this browser.');
      setMsg('');
    }
  };

  return (
    <Box className="p-6">
      <Typography variant="h5" sx={{ mb: 2 }}>Inclusive Intake</Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Citizen ID (optional)" value={citizenId} onChange={(e)=>setCitizenId(e.target.value)} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Chosen name" name="chosenName" value={form.chosenName} onChange={handle} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Legal name" name="legalName" value={form.legalName} onChange={handle} fullWidth />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField select label="Pronouns" name="pronouns" value={form.pronouns} onChange={handle} fullWidth>
            {pronounOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField label="Pronouns (if Other)" name="pronounsOther" value={form.pronounsOther} onChange={handle} fullWidth />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField select label="Sex assigned at birth" name="sexAssignedAtBirth" value={form.sexAssignedAtBirth} onChange={handle} fullWidth>
            {sabOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select label="Gender identity" name="genderIdentity" value={form.genderIdentity} onChange={handle} fullWidth>
            {giOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField select label="Sexual orientation (optional)" name="sexualOrientation" value={form.sexualOrientation} onChange={handle} fullWidth>
            {orientationOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField label="Safety concerns / violence screening" name="safetyConcerns" value={form.safetyConcerns} onChange={handle} fullWidth multiline />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Transition history (surgical/medical/none)" name="transitionHistory" value={form.transitionHistory} onChange={handle} fullWidth multiline />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Hormone therapy (name/dose/monitoring)" name="hormoneTherapy" value={form.hormoneTherapy} onChange={handle} fullWidth multiline />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Mental health screening (PHQ/GAD/etc.)" name="mentalHealthScreen" value={form.mentalHealthScreen} onChange={handle} fullWidth multiline />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Social support / family / community" name="socialSupport" value={form.socialSupport} onChange={handle} fullWidth multiline />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Notes" name="notes" value={form.notes} onChange={handle} fullWidth multiline />
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" onClick={doSave}>Save</Button>
        </Grid>
      </Grid>
    </Box>
  );
}
